<script setup>
import { onMounted, reactive } from "vue";
import { useFetch } from "@/plugins/fetch";

// Import new and existing components
import MasterCard from "@/components/Dashboard/Cards/MasterCard.vue";
import SubscriptionDetailsCard from "@/components/Billing/SubscriptionDetailsCard.vue";
import InvoicesTable from "@/components/Billing/InvoicesTable.vue";
import Skeleton from "primevue/skeleton";

const fetch = useFetch();

// Centralized state for the billing page
const state = reactive({
  loading: true,
  subscription: null,
  invoices: [],
  paymentMethod: null,
  error: null,
});

const fetchBillingData = async () => {
  state.loading = true;
  state.error = null;
  try {
    // Use Promise.allSettled to handle cases where some requests might fail
    const [subscriptionResult, invoicesResult] = await Promise.allSettled([
      fetch('user/subscription/'),
      fetch('user/invoices/')
    ]);

    if (subscriptionResult.status === 'fulfilled') {
      state.subscription = subscriptionResult.value;
      // Mock payment method based on subscription for now
      if(state.subscription) {
        state.paymentMethod = {
          number: "7852",
          holderName: "Card Holder",
          expiryDate: "01/25",
        }
      }
    } else {
      // Gracefully handle no subscription found without breaking the page
      if (subscriptionResult.reason?.response?.status !== 404) {
        console.error("Error fetching subscription:", subscriptionResult.reason);
        state.error = "Could not load subscription details.";
      }
    }

    if (invoicesResult.status === 'fulfilled') {
      state.invoices = invoicesResult.value;
    } else {
      // FIX: Gracefully handle no invoices found (404) without showing an error
      if (invoicesResult.reason?.response?.status !== 404) {
        console.error("Error fetching invoices:", invoicesResult.reason);
        state.error = state.error ? "Could not load billing information." : "Could not load invoice history.";
      }
    }

  } catch (err) {
    console.error("A critical error occurred while fetching billing data:", err);
    state.error = "An unexpected error occurred. Please try again later.";
  } finally {
    state.loading = false;
  }
};

onMounted(fetchBillingData);
</script>

<template>
  <div class="py-4 container-fluid">
    <!-- Loading State -->
    <div v-if="state.loading">
      <div class="row">
        <div class="col-lg-8">
          <div class="row">
            <div class="col-xl-6 mb-4"><Skeleton height="14rem" /></div>
            <div class="col-xl-6 mb-4"><Skeleton height="14rem" /></div>
            <div class="col-12"><Skeleton height="20rem" /></div>
          </div>
        </div>
        <div class="col-lg-4">
          <!-- Placeholder for future right-column content -->
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="state.error" class="alert alert-danger text-white">
      {{ state.error }}
    </div>

    <!-- Content Loaded State -->
    <div v-else>
      <div class="row">
<!--        <div class="col-lg-8">-->
<!--          <div class="row">-->
            <!-- Master Card -->
            <div class="col-xl-6 mb-xl-0 mb-4">
              <MasterCard v-if="state.paymentMethod" :card="{
                   number: state.paymentMethod.number,
                   holderName: state.paymentMethod.holderName,
                   expiryDate: state.paymentMethod.expiryDate,
                   holderText: 'Card Holder',
                   expiryText: 'Expires',
                   background: 'dark'
               }" />
              <div v-else class="card h-100 justify-content-center align-items-center">
                <div class="text-center">
                  <i class="fas fa-credit-card fa-2x text-muted mb-3"></i>
                  <h6 class="text-muted">No Payment Method</h6>
                  <p class="text-sm">Add a payment method to get started.</p>
                </div>
              </div>
            </div>
            <!-- Subscription Details -->
            <div class="col-xl-6">
              <SubscriptionDetailsCard :subscription="state.subscription" />
            </div>
            <!-- Invoice History -->
            <div class="col-md-12 mt-4">
              <InvoicesTable :invoices="state.invoices" />
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <!-- Future content can go here, like a "Billing Actions" card -->
        </div>
<!--      </div>-->
<!--    </div>-->
  </div>
</template>
